import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useLocationStore } from '@/store/location';
import { isPurchasingManagerEnabled } from '@/lib/organizationFeatures';

export type MorningBriefSignalType = 'inventory' | 'void_discount' | 'menu_margin' | 'slow_hour';
export type MorningBriefActionStatus = 'open' | 'reviewed' | 'actioned' | 'dismissed';

export interface MorningBriefSignal {
  key: string;
  type: MorningBriefSignalType;
  priority: 'high' | 'medium';
  title: string;
  summary: string;
  value?: number;
  valueLabel: string;
  actionLabel: string;
  actionHref: string;
  evidence: Record<string, unknown>;
  status?: MorningBriefActionStatus;
}

interface BriefOrder {
  id: string;
  location_id: string;
  created_at: string;
  total: number;
  discount_amount: number | null;
  status: string;
  voided_at: string | null;
  void_reason: string | null;
}

interface BriefOrderItem {
  product_id: string | null;
  quantity: number;
  total: number;
  order: Array<{ location_id: string; created_at: string; status: string }> | null;
}

interface BriefProduct {
  id: string;
  name: string;
  price: number;
  cost: number | null;
}

interface PurchasingRow {
  item_id: string;
  item_name: string;
  location_id: string;
  location_name: string;
  recommended_quantity: number | string;
  unit_of_measure: string;
  estimated_cost: number | string;
  confidence: 'low' | 'medium' | 'high';
  priority: 'urgent' | 'high' | 'overstock' | 'normal';
  reason: string;
}

interface StoredAction {
  signal_key: string;
  status: MorningBriefActionStatus;
}

const asNumber = (value: unknown) => Number(value || 0);
const dateKey = () => new Date().toISOString().slice(0, 10);

export function useMorningBrief() {
  const { organization, profile, user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const { currentLocation, locationScope } = useLocationStore();
  const queryClient = useQueryClient();
  const locationId = locationScope === 'single' ? currentLocation?.id || null : null;
  const since = useMemo(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), []);
  const purchasingManagerEnabled = isPurchasingManagerEnabled(organization?.settings, profile?.role)
    && hasAnyPermission(['inventory.purchasing.view', 'inventory.purchase_orders.view']);

  const ordersQuery = useQuery({
    queryKey: ['morning-brief-orders', organization?.id, locationId],
    queryFn: async () => {
      let query = supabase.from('orders').select('id, location_id, created_at, total, discount_amount, status, voided_at, void_reason').gte('created_at', since);
      if (locationId) query = query.eq('location_id', locationId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as BriefOrder[];
    },
    enabled: !!organization?.id,
  });

  const itemsQuery = useQuery({
    queryKey: ['morning-brief-order-items', organization?.id, locationId],
    queryFn: async () => {
      let query = supabase
        .from('order_items')
        .select('product_id, quantity, total, order:orders!inner(location_id, created_at, status)')
        .gte('order.created_at', since)
        .in('order.status', ['completed', 'ready', 'sent']);
      if (locationId) query = query.eq('order.location_id', locationId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as BriefOrderItem[];
    },
    enabled: !!organization?.id,
  });

  const productsQuery = useQuery({
    queryKey: ['morning-brief-products', organization?.id, locationId],
    queryFn: async () => {
      let query = supabase.from('products').select('id, name, price, cost').eq('is_active', true);
      if (locationId) query = query.eq('location_id', locationId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as BriefProduct[];
    },
    enabled: !!organization?.id,
  });

  const purchasingQuery = useQuery({
    queryKey: ['morning-brief-purchasing', organization?.id, locationId],
    queryFn: async () => {
      if (!organization?.id) return [] as PurchasingRow[];
      const { data, error } = await supabase.rpc('get_purchasing_manager_recommendations', {
        p_organization_id: organization.id,
        p_location_id: locationId,
        p_supplier_id: null,
      });
      if (error) throw error;
      return (data || []) as PurchasingRow[];
    },
    enabled: !!organization?.id && purchasingManagerEnabled,
    retry: false,
  });

  const actionsQuery = useQuery({
    queryKey: ['morning-brief-actions', organization?.id, locationId],
    queryFn: async () => {
      let query = supabase.from('morning_brief_actions').select('signal_key, status').eq('organization_id', organization!.id);
      if (locationId) query = query.eq('location_id', locationId);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as StoredAction[];
    },
    enabled: !!organization?.id,
  });

  const signals = useMemo(() => {
    const next: MorningBriefSignal[] = [];
    const day = dateKey();
    const orders = ordersQuery.data || [];
    const actionStatus = new Map((actionsQuery.data || []).map((action) => [action.signal_key, action.status]));

    (purchasingQuery.data || [])
      .filter((item) => (item.priority === 'urgent' || item.priority === 'high') && asNumber(item.recommended_quantity) > 0)
      .slice(0, 2)
      .forEach((item) => {
        const key = `inventory:${day}:${item.location_id}:${item.item_id}`;
        next.push({
          key,
          type: 'inventory',
          priority: item.priority === 'urgent' ? 'high' : 'medium',
          title: `Restock ${item.item_name}`,
          summary: item.reason,
          value: asNumber(item.estimated_cost),
          valueLabel: `${asNumber(item.recommended_quantity)} ${item.unit_of_measure} · ${item.confidence} confidence`,
          actionLabel: 'Review purchase plan',
          actionHref: '/stock-control/purchasing-manager',
          evidence: { item, as_of: new Date().toISOString() },
          status: actionStatus.get(key),
        });
      });

    const lastSevenDays = orders.filter((order) => new Date(order.created_at).getTime() >= Date.now() - 7 * 24 * 60 * 60 * 1000);
    const voids = lastSevenDays.filter((order) => order.status === 'voided');
    const discountAmount = lastSevenDays.reduce((sum, order) => sum + asNumber(order.discount_amount), 0);
    const voidAmount = voids.reduce((sum, order) => sum + asNumber(order.total), 0);
    if (voids.length > 0 || discountAmount > 0) {
      const key = `void_discount:${day}:${locationId || 'all'}`;
      next.push({
        key,
        type: 'void_discount',
        priority: voidAmount > 0 ? 'high' : 'medium',
        title: `${voids.length} void${voids.length === 1 ? '' : 's'} and discount activity need review`,
        summary: 'Review the last seven days of voids and discounts before they become a repeated margin leak.',
        value: voidAmount + discountAmount,
        valueLabel: '7-day review amount',
        actionLabel: 'Review sales activity',
        actionHref: '/reports/sales',
        evidence: { void_count: voids.length, void_amount: voidAmount, discount_amount: discountAmount, window_days: 7 },
        status: actionStatus.get(key),
      });
    }

    const productsById = new Map((productsQuery.data || []).map((product) => [product.id, product]));
    const productTotals = new Map<string, { sales: number; quantity: number }>();
    (itemsQuery.data || []).forEach((item) => {
      if (!item.product_id) return;
      const current = productTotals.get(item.product_id) || { sales: 0, quantity: 0 };
      current.sales += asNumber(item.total);
      current.quantity += asNumber(item.quantity);
      productTotals.set(item.product_id, current);
    });
    const marginRisk = [...productTotals.entries()]
      .map(([productId, totals]) => {
        const product = productsById.get(productId);
        if (!product || product.cost === null || product.price <= 0 || totals.quantity <= 0) return null;
        const marginRate = (totals.sales - asNumber(product.cost) * totals.quantity) / totals.sales;
        return { product, totals, marginRate };
      })
      .filter((row): row is NonNullable<typeof row> => !!row && row.marginRate < 0.5 && row.totals.sales > 0)
      .sort((a, b) => a.marginRate - b.marginRate)[0];
    if (marginRisk) {
      const key = `menu_margin:${day}:${locationId || 'all'}:${marginRisk.product.id}`;
      next.push({
        key,
        type: 'menu_margin',
        priority: 'medium',
        title: `Review margin for ${marginRisk.product.name}`,
        summary: `Its recorded cost and the last 30 days of sales produce a ${Math.round(marginRisk.marginRate * 100)}% gross margin before operating expenses.`,
        value: marginRisk.totals.sales,
        valueLabel: '30-day sales at risk',
        actionLabel: 'Review pricing',
        actionHref: '/stock-control/pricing',
        evidence: { product_id: marginRisk.product.id, sales: marginRisk.totals.sales, quantity: marginRisk.totals.quantity, cost: marginRisk.product.cost, margin_rate: marginRisk.marginRate, window_days: 30 },
        status: actionStatus.get(key),
      });
    }

    const completedOrders = orders.filter((order) => ['completed', 'ready', 'sent'].includes(order.status));
    const hourly = new Map<number, { sales: number; count: number }>();
    completedOrders.forEach((order) => {
      const hour = new Date(order.created_at).getHours();
      const current = hourly.get(hour) || { sales: 0, count: 0 };
      current.sales += asNumber(order.total);
      current.count += 1;
      hourly.set(hour, current);
    });
    const hours = [...hourly.entries()].filter(([, value]) => value.count >= 3).map(([hour, value]) => ({ hour, ...value }));
    const averageHourlySales = hours.length ? hours.reduce((sum, value) => sum + value.sales / value.count, 0) / hours.length : 0;
    const slowHour = hours.filter((value) => value.sales / value.count < averageHourlySales * 0.55).sort((a, b) => a.sales / a.count - b.sales / b.count)[0];
    if (slowHour) {
      const key = `slow_hour:${day}:${locationId || 'all'}:${slowHour.hour}`;
      const displayHour = new Date(2000, 0, 1, slowHour.hour).toLocaleTimeString([], { hour: 'numeric' });
      next.push({
        key,
        type: 'slow_hour',
        priority: 'medium',
        title: `${displayHour} is a recurring slow period`,
        summary: `Average order value in this hour is materially below the active-hour average over the last 30 days.`,
        value: slowHour.sales / slowHour.count,
        valueLabel: 'average order value',
        actionLabel: 'Create a targeted promotion',
        actionHref: '/stock-control/promotions/new',
        evidence: { hour: slowHour.hour, average_order_value: slowHour.sales / slowHour.count, active_hour_average: averageHourlySales, order_count: slowHour.count, window_days: 30 },
        status: actionStatus.get(key),
      });
    }

    return next.sort((a, b) => (a.priority === 'high' ? -1 : 1) - (b.priority === 'high' ? -1 : 1));
  }, [actionsQuery.data, itemsQuery.data, locationId, ordersQuery.data, productsQuery.data, purchasingQuery.data]);

  const updateAction = useMutation({
    mutationFn: async ({ signal, status, note }: { signal: MorningBriefSignal; status: MorningBriefActionStatus; note?: string }) => {
      if (!organization?.id || !user?.id) throw new Error('Organization context is required.');
      const { error } = await supabase.from('morning_brief_actions').upsert({
        organization_id: organization.id,
        location_id: locationId,
        signal_key: signal.key,
        signal_type: signal.type,
        title: signal.title,
        evidence: signal.evidence,
        status,
        created_by: user.id,
        acted_by: status === 'open' ? null : user.id,
        acted_at: status === 'open' ? null : new Date().toISOString(),
        resolution_note: note || null,
      }, { onConflict: 'organization_id,location_id,signal_key' });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['morning-brief-actions'] }),
  });

  return {
    signals,
    isLoading: ordersQuery.isLoading || itemsQuery.isLoading || productsQuery.isLoading || purchasingQuery.isLoading,
    reviewAmount: signals.filter((signal) => signal.type === 'void_discount').reduce((sum, signal) => sum + (signal.value || 0), 0),
    updateAction,
  };
}
